// MCO-only Add Plan pricing — no optimizeInsurance. Rows are shaped for
// InsuranceCalculatePremiumEndorsementsTableGrid (UI reuse only).

import { MCOService } from "components/Insurance/MCOService";

import { loadMcoAreaBundle, loadMcoProjectedPriceFromAdm, MCO_PLAN_NAMES } from "./mcoAreaInputs.js";
import { loadMcoActuarialAndCropLeg } from "./mcoOfficialData.js";
import { computeMcoProducerPremium } from "./mcoPremium.js";
import { mcoSupplementOptionToGridRow } from "./mcoSupplementGrid.js";
import { defaultMcoBandsForYear, num, toShareDecimal } from "./mcoShared.js";

export { MCO_UNDERLYING_COVERAGE_LEVELS } from "./mcoSupplementGrid.js";
export { mcoSupplementOptionToGridRow } from "./mcoSupplementGrid.js";

function cropFromParams(farmParameters, countyParameters) {
  return {
    stateName: farmParameters?.state_name || farmParameters?.location_state_name,
    countyName: farmParameters?.county_name || farmParameters?.location_county_name,
    commodityName: farmParameters?.commodity_name,
    type_name: farmParameters?.type_name,
    practice_name: farmParameters?.practice_name,
    acres: num(farmParameters?.reported_acreage),
    yieldPerAcre: num(farmParameters?.approved_yield),
    sharePct: farmParameters?.share_percent,
    price: countyParameters?.projected_price ?? farmParameters?.projected_price,
  };
}

/**
 * @param {object} opts
 * @param {object} opts.farmParameters
 * @param {object} opts.countyParameters
 * @param {object} opts.supplementOffer — from loadAreaSupplementOffers
 * @param {number} opts.underlyingCoverage — intended RP/YP coverage (0.75 etc)
 */
export async function buildMcoSupplementPlanOptions({
  farmParameters,
  countyParameters,
  supplementOffer,
  underlyingCoverage,
}) {
  const planCode = String(supplementOffer?.planCode || "68");
  const reinsuranceYear = String(
    countyParameters?.reinsurance_year || farmParameters?.reinsurance_year || farmParameters?.year || ""
  );
  const crop = cropFromParams(
    {
      ...farmParameters,
      practice_name: supplementOffer?.practice_name || farmParameters?.practice_name,
    },
    countyParameters
  );
  const acres = crop.acres;
  const approvedYield = crop.yieldPerAcre;
  const underlyingCov = num(underlyingCoverage);

  if (!(acres > 0) || !(approvedYield > 0) || !(underlyingCov > 0)) {
    return { rows: [], blockers: ["Farm acres, approved yield, and underlying coverage are required"] };
  }

  let projectedPrice = num(crop.price);
  if (!(projectedPrice > 0)) {
    projectedPrice = num(await loadMcoProjectedPriceFromAdm(crop, reinsuranceYear, {
      insurancePlanName: supplementOffer?.insurance_plan_name,
    }));
  }
  if (!(projectedPrice > 0)) {
    return { rows: [], blockers: ["MCO projected price not available yet"] };
  }

  const bands = supplementOffer?.bands?.length ? supplementOffer.bands : defaultMcoBandsForYear(reinsuranceYear);
  const shareDecimal = toShareDecimal(farmParameters?.share_percent);
  const underlyingIntentLabel = `${supplementOffer?.basePlanName || "MPCI"} ${Math.round(underlyingCov * 100)}% (intended — not on sale yet)`;

  const mcoService = MCOService().actions;
  const rows = [];
  const blockers = [];

  for (const band of bands) {
    let officialSubsidyPercent = null;
    try {
      const { actuarial } = await loadMcoActuarialAndCropLeg(mcoService, {
        crop_year: reinsuranceYear,
        state_name: crop.stateName,
        county_name: crop.countyName,
        commodity_name: crop.commodityName,
        type_name: crop.type_name,
        practice_name: supplementOffer?.practice_name || crop.practice_name,
        insurance_plan_code: planCode,
        approved_yield: approvedYield,
        area_loss_order: band.order || "1",
        area_loss_start_percent: band.start,
        area_loss_end_percent: band.end,
        protection_factor: 1,
      });
      officialSubsidyPercent = num(actuarial?.subsidyrate ?? actuarial?.subsidyRate);
    } catch {
      // ADM / Official may lag; loadMcoAreaBundle tries both
    }

    const areaBundle = await loadMcoAreaBundle(
      { ...crop, price: projectedPrice, practice_name: supplementOffer?.practice_name || crop.practice_name },
      {
        planCode,
        coverageStart: band.start,
        areaLossEnd: band.end,
        protectionFactor: 1,
        officialSubsidyPercent,
        areaLossOrder: band.order,
      },
      reinsuranceYear
    );

    if (!areaBundle.ready) {
      blockers.push(...(areaBundle.blockers || []));
      continue;
    }

    const premium = computeMcoProducerPremium(areaBundle.bundle);
    if (!premium.ready) continue;

    const mcoPlan = {
      insurance_plan_name: areaBundle.bundle.planName || MCO_PLAN_NAMES[planCode]?.[0],
      insurance_plan_code: planCode,
      coverage_level_percent: band.start,
      area_loss_end_percent: band.end,
      practice_name: supplementOffer?.practice_name,
      reported_acreage: acres,
      approved_yield: approvedYield,
      projected_price: areaBundle.bundle.projectedPrice,
      insured_share_percent: shareDecimal,
      liability_amount: premium.result.liabilityAmount,
      liability_amount_per_acre: premium.result.liabilityAmount / acres,
      producer_premium_amount: premium.result.producerPremiumAmount,
      producer_premium_amount_per_acre: premium.result.producerPremiumAmount / acres,
    };

    rows.push(mcoSupplementOptionToGridRow({ mcoPlan, acres, underlyingIntentLabel }));
  }

  return {
    rows,
    blockers: rows.length ? [] : [...new Set(blockers)],
  };
}
